import type {
  ExecuteKeytipEventHandler,
  KeytipProps,
  ReturnKeytipEventHandler,
} from "@fluentui-contrib/react-keytips";
import { memoizeFunction } from "@fluentui/react/lib/Utilities";
import { DirectionalHint } from "./directionalHint";

const tabOffset = 24; // Tab header keytips should be positioned 24px from the top
const fileOffset = 26; // File keytips should be positioned 26px from the top

export const getDefaultKeytipProps = (
  keytipProps: KeytipProps & {
    calloutProps?: {
      directionalHint?: number;
      coverTarget?: boolean;
      gapSpace?: number;
    };
  },
  _offset: number,
  onExecute?: (executeTarget: HTMLElement, target: HTMLElement) => void,
  directionalHint?: number
): KeytipProps => {
  let directionalHintValue =
    (directionalHint as keyof typeof DirectionalHint) ||
    keytipProps.calloutProps?.directionalHint;
  const direction =
    directionalHintValue || directionalHintValue === 0
      ? DirectionalHint[directionalHintValue]
      : DirectionalHint[2];
  return {
    onReturn: defaultOnReturn as unknown as ReturnKeytipEventHandler,
    ...keytipProps,
    onExecute: onExecute as unknown as ExecuteKeytipEventHandler,
    positioning: {
      coverTarget: keytipProps.calloutProps?.coverTarget ?? false,
      offset: {
        mainAxis: -(keytipProps.calloutProps?.gapSpace || 0),
        crossAxis: 0,
      },
      position: direction.position,
      align: direction.align,
    },
  };
};

export const defaultOnReturn = (executeTarget: HTMLElement) =>
  executeTarget && executeTarget.focus();

export const deriveTabKeytip = memoizeFunction(
  (
    keytipProps?: KeytipProps,
    onExecute?: (executeTarget: HTMLElement) => void
  ): KeytipProps | undefined => {
    return keytipProps
      ? getDefaultKeytipProps(keytipProps, tabOffset, onExecute)
      : undefined;
  }
);

export const deriveFileKeytip = memoizeFunction(
  (
    keytipProps?: KeytipProps,
    onExecute?: (executeTarget: HTMLElement) => void
  ): KeytipProps | undefined => {
    return keytipProps
      ? getDefaultKeytipProps(keytipProps, fileOffset, onExecute)
      : undefined;
  }
);

export const deriveInputKeytip = memoizeFunction(
  (
    keytipProps?: KeytipProps,
    onExecute?: (executeTarget: HTMLElement) => void
  ): KeytipProps | undefined => {
    return keytipProps
      ? getDefaultKeytipProps(keytipProps, 26, onExecute)
      : undefined;
  }
);
